import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { LogOut, User, Flame, Activity, Sparkles, ChevronLeft } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { streakApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { scheduleStreakReminder, clearReminder } from '@/lib/notifications';
import { LOGOUT } from '@/constants/testIds';

const MILESTONES = [
  { days: 3, label: 'Three days in — the habit is forming.', emoji: '🌱' },
  { days: 7, label: 'One full week. This is who you are now.', emoji: '🔥' },
  { days: 14, label: 'Two weeks strong. Momentum is compounding.', emoji: '💫' },
  { days: 30, label: 'Thirty days. Welcome to the top 1% of consistency.', emoji: '🏆' },
  { days: 60, label: 'Sixty days. You are the kind of person who follows through.', emoji: '⚡' },
  { days: 100, label: 'One hundred days. Legendary. This is wealth.', emoji: '👑' },
  { days: 365, label: 'A full year of Wellth. You built something rare.', emoji: '🌟' },
];

const CELEBRATED_KEY = 'wellth_streak_milestones_celebrated';

const Navbar = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [streak, setStreak] = useState(0);

  useEffect(() => {
    if (!user) { setStreak(0); clearReminder(); return; }
    streakApi.ping().then((s) => {
      const days = s.streak_days || 0;
      setStreak(days);
      scheduleStreakReminder({ streakDays: days, lastActiveISO: s.streak_last_active });
      // Celebrate milestone once per user per level
      const celebrated = JSON.parse(localStorage.getItem(CELEBRATED_KEY) || '{}');
      const key = user.id;
      const alreadyForUser = celebrated[key] || [];
      const hit = MILESTONES.find((m) => m.days === days && !alreadyForUser.includes(m.days));
      if (hit) {
        toast.success(`${hit.emoji}  ${days}-day streak!`, { description: hit.label, duration: 6000 });
        celebrated[key] = [...alreadyForUser, hit.days];
        localStorage.setItem(CELEBRATED_KEY, JSON.stringify(celebrated));
      }
    }).catch(() => {});
    return () => clearReminder();
  }, [user]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <header className="sticky top-0 z-40 border-b border-[#E5E1D8] bg-[#FAF9F6]/85 backdrop-blur-md">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 sm:gap-3 min-w-0">
          {location.pathname !== '/' && (
            <button
              type="button"
              onClick={() => (window.history.state?.idx > 0 ? navigate(-1) : navigate('/'))}
              aria-label="Go back"
              className="group shrink-0 inline-flex items-center gap-1 h-9 pl-2 pr-2 sm:pr-3 rounded-full border border-[#E5E1D8] bg-white text-[#4A5A53] hover:text-[#4A7C59] hover:border-[#7E9979] transition-colors"
              data-testid="navbar-back-button"
            >
              <ChevronLeft className="w-4 h-4 transition-transform group-hover:-translate-x-0.5" strokeWidth={1.75} />
              <span className="hidden sm:inline text-sm font-medium">Back</span>
            </button>
          )}
          {location.pathname !== '/' && <span className="hidden sm:block w-px h-6 bg-[#E5E1D8]" aria-hidden="true" />}
          <Link to="/" className="flex items-center gap-2.5 group min-w-0" data-testid="navbar-brand">
            <img
              src="/wellth-logo.png"
              alt="Wellth"
              className="w-9 h-9 rounded-lg object-cover shrink-0"
              data-testid="navbar-logo"
            />
            <span className={`font-serif-display text-xl sm:text-2xl font-medium tracking-tight text-[#1A2421] ${location.pathname !== '/' && !user ? 'hidden min-[420px]:inline' : ''}`}>Wellth<span className="text-[#4A7C59]">AI</span></span>
          </Link>
        </div>

        <nav className="flex items-center gap-1.5 sm:gap-4 shrink-0">
          {user ? (
            <>
              {/* Streak pill */}
              <div
                className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-[#E5E1D8]"
                title={`${streak}-day streak`}
                data-testid="navbar-streak-pill"
              >
                <Flame
                  className={`w-4 h-4 ${streak > 0 ? 'text-[#C05746] fill-[#C05746]' : 'text-[#4A5A53]'}`}
                  strokeWidth={1.5}
                />
                <span className="text-sm font-semibold text-[#1A2421] tabular-nums" data-testid="navbar-streak-count">
                  {streak}
                </span>
              </div>

              <Link
                to="/tracker"
                className={`text-sm font-medium hidden md:inline-flex items-center gap-1.5 px-3 py-2 rounded-full transition-colors ${location.pathname === '/tracker' ? 'text-[#4A7C59]' : 'text-[#4A5A53] hover:text-[#4A7C59]'}`}
                data-testid="navbar-tracker-link"
              >
                <Activity className="w-4 h-4" strokeWidth={1.5} /> Tracker
              </Link>
              <Link
                to="/avatar"
                className={`text-sm font-medium hidden md:inline-flex items-center gap-1.5 px-3 py-2 rounded-full transition-colors ${location.pathname === '/avatar' ? 'text-[#4A7C59]' : 'text-[#4A5A53] hover:text-[#4A7C59]'}`}
                data-testid="navbar-avatar-link"
              >
                <Sparkles className="w-4 h-4" strokeWidth={1.5} /> Avatar
              </Link>
              <Link
                to="/dashboard"
                className={`text-sm font-medium hidden md:inline-flex items-center gap-1.5 px-3 py-2 rounded-full transition-colors ${location.pathname === '/dashboard' ? 'text-[#4A7C59]' : 'text-[#4A5A53] hover:text-[#4A7C59]'}`}
                data-testid="navbar-dashboard-link"
              >
                <User className="w-4 h-4" strokeWidth={1.5} /> My History
              </Link>
              {user.picture && (
                <img
                  src={user.picture}
                  alt={user.name}
                  referrerPolicy="no-referrer"
                  className="w-8 h-8 rounded-full object-cover border border-[#E5E1D8]"
                  data-testid="navbar-user-photo"
                />
              )}
              <span className="text-xs text-[#4A5A53] hidden lg:inline" data-testid="navbar-user-name">Hi, {user.name.split(' ')[0]}</span>
              <Button
                onClick={handleLogout}
                variant="ghost"
                size="sm"
                className="rounded-full text-[#4A5A53] hover:text-[#C05746]"
                data-testid={LOGOUT.button}
              >
                <LogOut className="w-4 h-4" strokeWidth={1.5} />
              </Button>
            </>
          ) : (
            <>
              <Link to="/login" className="text-sm font-medium text-[#4A5A53] hover:text-[#4A7C59] px-2 sm:px-3 py-2 whitespace-nowrap" data-testid="navbar-login-link">
                Sign in
              </Link>
              <Link to="/signup">
                <Button className="rounded-full bg-[#4A7C59] hover:bg-[#3E6B4F] text-white px-4 sm:px-5 py-2 h-9 text-sm whitespace-nowrap" data-testid="navbar-signup-button">
                  <span className="sm:hidden">Join</span><span className="hidden sm:inline">Create account</span>
                </Button>
              </Link>
            </>
          )}
        </nav>
      </div>
    </header>
  );
};

export default Navbar;
